import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Compass, ArrowRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line no-unused-vars
const destinationsData = [
  {
    id: 'varanasi',
    title: 'Varanasi',
    state: 'Uttar Pradesh',
    tagline: 'The Eternal City of Light and Moksha',
    image: '/images/varanasi.png',
    description: 'Mark Twain wrote, "Varanasi is older than history, older than tradition, older even than legend, and looks twice as old as all of them put together." Situated on the banks of the sacred Ganges, Varanasi is the spiritual beating heart of India, where life and death meet in plain sight.',
    spiritualEssence: 'To experience Varanasi is to confront the cycle of existence. The burning ghats remind us of the impermanence of the physical body, while the evening Ganga Aarti celebration offers a magnificent display of gratitude to the river goddess.',
    activities: [
      'Witness the hypnotic Ganga Aarti at Dashashwamedh Ghat.',
      'Take a silent sunrise boat ride along the ancient ghats.',
      'Wander through the labyrinthine streets of the Kashi Vishwanath temple corridor.',
      'Meditate at Sarnath, where Lord Buddha preached his first sermon.'
    ],
    bestTime: 'October to March (for pleasant cool breezes)',
    signatureExperience: 'Sunrise Boat Meditation: Watch the city awaken to chants, ringing bells, and the soft amber light reflecting on the sacred waters.'
  },
  {
    id: 'rishikesh',
    title: 'Rishikesh',
    state: 'Uttarakhand',
    tagline: 'The Yoga Capital of the World',
    image: '/images/rishikesh.png',
    description: 'Cradled in the foothills of the Himalayas and divided by the emerald green waters of the Ganges, Rishikesh has been a refuge for yogis, sages, and seekers for thousands of years. The Beatles famously put it on the global map in 1968, but its roots go back to Vedic antiquity.',
    spiritualEssence: 'Rishikesh acts as a natural crucible for inner transformation. The thin mountain air, the sound of flowing glacial waters, and the presence of numerous ancient ashrams create a field of high vibrational energy ideal for meditation and yoga.',
    activities: [
      'Participate in traditional Satsang and meditation at Parmarth Niketan Ashram.',
      'Learn ancient Hatha and Ashtanga yoga techniques from traditional masters.',
      'Hike to the sacred Vashishta Gufa (cave) for deep silent meditation.',
      'Cross the iconic Lakshman Jhula suspension bridge over the Ganges.'
    ],
    bestTime: 'September to November, and March to May',
    signatureExperience: 'Beatles Ashram Exploration: Wander through the ruins of Maharishi Mahesh Yogi\'s ashram, where graffiti and nature have reclaimed the stone meditation domes.'
  },
  {
    id: 'kerala',
    title: 'Kerala Wellness',
    state: 'Kerala',
    tagline: 'Land of Ayurveda and Ancient Healing',
    image: '/images/kerala.png',
    description: 'Ayurveda, the "science of life," has been practiced continuously in Kerala for over 5,000 years. Surrounded by lush tropical spice gardens, tranquil backwaters, and pristine beaches, Kerala offers a total alignment of mind, body, and spirit.',
    spiritualEssence: 'Healing is approached as a spiritual practice in Ayurveda. It focuses on balancing the five elements within us through customized herbal oils, organic diets, and therapeutic massages that cleanse toxic accumulations and release stored emotional stress.',
    activities: [
      'Undergo a personalized Panchakarma detoxification process.',
      'Practice Pranayama and yoga amidst coco-palms at sunrise.',
      'Consult with traditional Vaidyas (Ayurvedic physicians) to discover your Dosha.',
      'Cruise on a luxury wooden houseboat along the spiritual backwaters of Alappuzha.'
    ],
    bestTime: 'June to November (Monsoon is considered the ideal time for Ayurvedic therapies)',
    signatureExperience: 'Shirodhara Treatment: Feel a warm, continuous stream of therapeutic herbal oil poured onto your forehead, calming the nervous system into a state of deep, blissful samadhi.'
  },
  {
    id: 'amritsar',
    title: 'Amritsar',
    state: 'Punjab',
    tagline: 'The Golden Sanctuary of Sewa (Selfless Service)',
    image: '/images/amritsar.png',
    description: 'Home to the Sri Harmandir Sahib (The Golden Temple), Amritsar represents the heart of Sikh spirituality. The temple, clad in pure gold, floats in a vast holy pool (Sarovar) and has four entrances symbolizing that all people from all walks of life are welcome.',
    spiritualEssence: 'Amritsar is an experiential lesson in absolute equality and humility. The temple kitchen (Langar) feeds over 100,000 people daily for free, operated entirely by volunteers. Experiencing this selflessness is a profound spiritual cleansing.',
    activities: [
      'Partake in the Langar (community kitchen) service: chopping vegetables or washing dishes.',
      'Bathe in the sacred Sarovar waters believed to possess spiritual healing properties.',
      'Listen to the live, continuous Kirtan (devotional singing) echoing over the waters.',
      'Watch the night palanquin ceremony (Palki Sahib) carrying the holy scriptures.'
    ],
    bestTime: 'November to March',
    signatureExperience: 'Midnight Langar Volunteerism: Sit side-by-side on the floor with thousands of strangers, eating a simple meal cooked with love, and feel the boundaries of ego melt away.'
  },
  {
    id: 'madurai',
    title: 'Madurai',
    state: 'Tamil Nadu',
    tagline: 'The Monumental Portal of Dravidian Sacred Geometry',
    image: '/images/madurai.png',
    description: 'Madurai is one of the oldest continuously inhabited cities in the world. Dominated by the jaw-dropping Meenakshi Amman Temple, the city is laid out in the shape of a lotus, with the temple at its center, representing the divine cosmos itself.',
    spiritualEssence: 'The towering Gopurams (temple towers) covered in thousands of colorful mythological figures act as physical cosmic ladders. Entering the inner chambers, smelling jasmine and incense, and hearing chanting priests connects seekers to the deep feminine energy (Shakti).',
    activities: [
      'Explore the Hall of a Thousand Pillars, marveling at the acoustic stone sculptures.',
      'Witness the night ceremony where Lord Shiva is carried to Meenakshi\'s chamber.',
      'Walk the concentric stone corridors in silent reflection.',
      'Browse the vibrant flower and incense markets surrounding the temple walls.'
    ],
    bestTime: 'October to March',
    signatureExperience: 'Pooja Chanting Session: Stand in the central shrine under the golden dome, feeling the acoustic vibrations of Sanskrit chants resonate directly in your chest.'
  },
  {
    id: 'dharamshala',
    title: 'Dharamshala',
    state: 'Himachal Pradesh',
    tagline: 'The Himalayan Sanctuary of Tibetan Mindfulness',
    image: '/images/dharamshala.png',
    description: 'Nestled high in the Dhauladhar range of the Himalayas, Dharamshala (specifically McLeod Ganj) is the exile home of His Holiness the Dalai Lama. It is a vibrant center for Tibetan Buddhist studies, meditation, and mindful living, surrounded by prayer flags and pine forests.',
    spiritualEssence: 'Dharamshala teaches the path of compassion, mindfulness, and inner peace. The slow, rhythmic spinning of prayer wheels and the chanting of "Om Mani Padme Hum" by Buddhist monks creates a serene atmosphere of mental clarity and kindness.',
    activities: [
      'Walk the Kora (sacred circumambulation path) around the Dalai Lama Temple.',
      'Attend public teachings by the Dalai Lama (when scheduled) or senior lamas.',
      'Learn Tibetan Buddhist meditation at Tushita Meditation Centre.',
      'Trek to Triund for silent meditation amidst snow-covered Himalayan peaks.'
    ],
    bestTime: 'March to June, and September to November',
    signatureExperience: 'Kora Walk at Dawn: Walk the forested path alongside Tibetan elders, spinning copper prayer wheels and breathing the crisp, cedar-scented Himalayan air.'
  }
];

export default function Destinations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedDest, setSelectedDest] = useState(null);
  const [sanctuaries, setSanctuaries] = useState([]);
  const [showCustomizePopup, setShowCustomizePopup] = useState(false);
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    setShowCustomizePopup(true);
    const popupTimer = setTimeout(() => {
      setShowCustomizePopup(false);
    }, 5000);

    return () => {
      clearTimeout(popupTimer);
      document.body.style.overflow = 'auto';
      if (window.lenis) window.lenis.start();
    };
  }, []);

  useEffect(() => {
    const fetchSanctuaries = async () => {
      try {
        const res = await fetch(`${apiBase}/api/sanctuaries`);
        if (res.ok) {
          const data = await res.json();
          const dbSanctuaries = data.map(s => ({
            id: s.id.toString(),
            title: s.title,
            state: s.state,
            tagline: s.tagline,
            image: s.image,
            description: s.description,
            spiritualEssence: s.spiritualEssence,
            activities: s.activities || [],
            bestTime: s.bestTime,
            signatureExperience: s.signatureExperience
          }));

          setSanctuaries(dbSanctuaries);
        }
      } catch (err) {
        console.error("Error fetching sanctuaries:", err);
      }
    };

    fetchSanctuaries();
  }, [apiBase]);

  const handleOpenModal = (dest) => {
    setSelectedDest(dest);
    document.body.style.overflow = 'hidden'; // Lock background scroll
    if (window.lenis) window.lenis.stop();
  };

  const handleCloseModal = () => {
    setSelectedDest(null);
    document.body.style.overflow = 'auto'; // Unlock background scroll
    if (window.lenis) window.lenis.start();
  };

  return (
    <section
      id="destinations"
      style={{
        padding: '100px 0',
        backgroundColor: 'var(--bg-dark)',
        position: 'relative'
      }}
    >
      <div
        style={{
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <span
            className="text-gold"
            style={{
              textTransform: 'uppercase',
              letterSpacing: '0.3em',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}
          >
            {t('destinations.header')}
          </span>
          <h2
            className="font-serif"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              marginTop: '10px',
              color: 'var(--text-light)'
            }}
          >
            {t('destinations.title')}
          </h2>
          <div
            style={{
              width: '60px',
              height: '2px',
              backgroundColor: 'var(--primary-gold)',
              margin: '20px auto 0'
            }}
          />
        </div>

        {/* Grid */}
        <div className="responsive-grid grid-cols-3">
          {sanctuaries.map((dest) => {
            const translatedTitle = t(`destinations.items.${dest.id}.title`, { defaultValue: dest.title });
            const translatedState = t(`destinations.items.${dest.id}.state`, { defaultValue: dest.state });
            const translatedTagline = t(`destinations.items.${dest.id}.tagline`, { defaultValue: dest.tagline });
            const translatedDescription = t(`destinations.items.${dest.id}.description`, { defaultValue: dest.description });

            return (
              <div
                key={dest.id}
                className="glass-card card-hover-effect"
                style={{
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '450px',
                  position: 'relative',
                  transition: 'var(--transition-smooth)'
                }}
              >
                {/* Card Image */}
                <div
                  style={{
                    height: '55%',
                    width: '100%',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <img
                    src={dest.image}
                    alt={dest.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.8s ease'
                    }}
                    className="card-image"
                  />
                  {/* Location Tag */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '15px',
                      left: '15px',
                      backgroundColor: 'rgba(0, 0, 0, 0.86)',
                      color: '#ffffff',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.7rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      border: '1px solid rgba(212, 175, 55, 0.3)'
                    }}
                  >
                    <MapPin size={12} className="text-gold" />
                    {translatedState}
                  </div>
                </div>

                {/* Card Body */}
                <div
                  style={{
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <h3 className="font-serif text-gold" style={{ fontSize: '1.5rem', marginBottom: '8px' }}>
                      {translatedTitle}
                    </h3>
                    <p
                      style={{
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        color: 'var(--primary-saffron)',
                        marginBottom: '12px',
                        letterSpacing: '0.05em'
                      }}
                    >
                      {translatedTagline}
                    </p>
                    <p
                      style={{
                        fontSize: '0.85rem',
                        color: 'var(--text-muted)',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '1.5'
                      }}
                    >
                      {translatedDescription}
                    </p>
                  </div>

                  {/* Card Button */}
                  <button
                    onClick={() => handleOpenModal(dest)}
                    className="btn-link"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary-gold)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      width: 'fit-content',
                      marginTop: '15px'
                    }}
                  >
                    {t('destinations.btnEnter')} <ArrowRight size={14} className="arrow-icon" />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Custom Journey CTA Card */}
          <div
            className="glass-card card-hover-effect"
            onClick={() => navigate('/planner')}
            style={{
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '450px',
              position: 'relative',
              transition: 'var(--transition-smooth)',
              border: '1px dashed var(--primary-gold)',
              background: 'radial-gradient(circle at top right, rgba(212, 175, 55, 0.08) 0%, rgba(10, 8, 6, 0.35) 100%)',
              cursor: 'pointer',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              textAlign: 'center'
            }}
          >
            <Compass size={64} style={{ color: 'var(--primary-gold)', marginBottom: '24px', opacity: 0.9 }} className="arrow-icon" />
            <h3 className="font-serif text-gold" style={{ fontSize: '1.8rem', marginBottom: '12px' }}>
              {t('destinations.planCustom')}
            </h3>
            <p
              style={{
                fontSize: '0.85rem',
                color: 'var(--text-muted)',
                lineHeight: '1.6',
                marginBottom: '30px',
                maxWidth: '280px'
              }}
            >
              {t('destinations.planDesc')}
            </p>
            <button
              className="btn-gold"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/planner');
              }}
              style={{
                padding: '12px 28px',
                borderRadius: '30px',
                fontSize: '0.8rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {t('destinations.btnBegin')} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Modal Detail view */}
      {selectedDest && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(10, 8, 6, 0.9)',
            padding: '20px'
          }}
          onClick={handleCloseModal}
        >
          <div
            className="glass-card modal-content"
            data-lenis-prevent
            style={{
              width: '100%',
              maxWidth: '850px',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              animation: 'fadeIn 0.4s cubic-bezier(0.25, 1, 0.5, 1)'
            }}
            onClick={(e) => e.stopPropagation()} // Stop bubbling
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(10, 8, 6, 0.6)',
                border: '1px solid var(--primary-gold-border)',
                color: 'var(--text-light)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <X size={20} />
            </button>

            {/* Modal Hero Banner */}
            <div style={{ height: '300px', position: 'relative', width: '100%' }}>
              <img
                src={selectedDest.image}
                alt={selectedDest.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />

              <div style={{ position: 'absolute', bottom: '24px', left: '30px' }}>
                <span
                  style={{
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.2em',
                    color: 'var(--primary-saffron)',
                    fontWeight: '600'
                  }}
                >
                  {t(`destinations.items.${selectedDest.id}.state`, { defaultValue: selectedDest.state })}
                </span>
                <h2 className="font-serif text-gradient" style={{ fontSize: '2.5rem', marginTop: '5px' }}>
                  {t(`destinations.items.${selectedDest.id}.title`, { defaultValue: selectedDest.title })}
                </h2>
              </div>
            </div>

            {/* Modal Body */}
            <div className="modal-padding" style={{ padding: '30px' }}>
              <p style={{ fontStyle: 'italic', color: 'var(--primary-gold)', fontSize: '1.05rem', marginBottom: '20px' }}>
                "{t(`destinations.items.${selectedDest.id}.tagline`, { defaultValue: selectedDest.tagline })}"
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '30px',
                  marginBottom: '30px'
                }}
              >
                {/* Story */}
                <div>
                  <h4 className="font-serif text-saffron" style={{ fontSize: '1.2rem', marginBottom: '10px' }}>{t('destinations.modal.spiritualCall')}</h4>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: '1.7', marginBottom: '15px', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {t(`destinations.items.${selectedDest.id}.description`, { defaultValue: selectedDest.description })}
                  </p>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-light)', lineHeight: '1.7', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {t(`destinations.items.${selectedDest.id}.spiritualEssence`, { defaultValue: selectedDest.spiritualEssence })}
                  </p>
                </div>

                {/* Practical details */}
                <div style={{ background: 'rgba(255, 255, 255, 0.45)', padding: '20px', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.15)' }}>
                  <h4 className="font-serif text-gold" style={{ fontSize: '1.2rem', marginBottom: '15px' }}>{t('destinations.modal.journeyDetails')}</h4>

                  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <Calendar size={18} className="text-saffron" style={{ flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <h5 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-light)' }}>{t('destinations.modal.bestTime')}</h5>
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{t(`destinations.items.${selectedDest.id}.bestTime`, { defaultValue: selectedDest.bestTime })}</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <Compass size={18} className="text-saffron" style={{ flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <h5 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-light)' }}>{t('destinations.modal.essentialExp')}</h5>
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{t(`destinations.items.${selectedDest.id}.signatureExperience`, { defaultValue: selectedDest.signatureExperience })}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rituals/Activities */}
              <div style={{ borderTop: '1px solid rgba(212, 175, 55, 0.15)', paddingTop: '20px' }}>
                <h4 className="font-serif text-gold" style={{ fontSize: '1.2rem', marginBottom: '15px' }}>{t('destinations.modal.rituals')}</h4>
                <ul style={{ listStyleType: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(t(`destinations.items.${selectedDest.id}.activities`, { returnObjects: true, defaultValue: selectedDest.activities })).map((act, index) => (
                    <li key={index} style={{ fontSize: '0.9rem', color: 'var(--text-muted)', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span className="text-gold" style={{ fontWeight: 'bold' }}>•</span>
                      <span style={{ minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{act}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Expert Call and Custom Plans CTA */}
              <div style={{ borderTop: '1px solid rgba(212, 175, 55, 0.15)', paddingTop: '20px', marginTop: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                <button
                  className="btn-whatsapp"
                  onClick={() => navigate('/booking')}
                  style={{
                    flex: '1',
                    padding: '12px 20px',
                    borderRadius: '30px',
                    fontSize: '0.85rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  WhatsApp
                </button>
                <button
                  className="btn-saffron"
                  onClick={() => navigate('/planner')}
                  style={{
                    flex: '1',
                    padding: '12px 20px',
                    borderRadius: '30px',
                    fontSize: '0.85rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  Request Custom Plan
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Customize Journey Popup */}
      {showCustomizePopup && createPortal(
        <div style={{
          position: 'fixed',
          top: '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10000,
          pointerEvents: 'none'
        }}>
          <div
            onClick={() => navigate('/planner')}
            style={{
              background: 'linear-gradient(135deg, rgba(252, 252, 252, 0.95) 0%, rgba(181,137,37,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 235, 235, 1)',
              color: '#1a1508',
              padding: '12px 24px',
              borderRadius: '100px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)',
              fontWeight: '600',
              fontSize: '0.95rem',
              animation: 'fadeIn 0.5s ease-out',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              pointerEvents: 'auto',
              cursor: 'pointer'
            }}>
            <Compass size={18} />
            customize your spiritual journey!
          </div>
        </div>,
        document.body
      )}

      {/* Card hover styles */}
      <style>{`


        .card-hover-effect {
          position: relative;
          z-index: 1;
          transition: all 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
          border: 1px solid rgba(168, 148, 103, 0.82) !important;
          background: rgba(248, 248, 248, 1) !important;
          backdrop-filter: blur(12px) !important;
          -webkit-backdrop-filter: blur(12px) !important;
        }

        .card-hover-effect:hover {
          z-index: 12 !important;
          transform: translateY(-10px) scale(1.03);
          border-color: rgba(212, 175, 55, 0.6) !important;
          box-shadow: 0 25px 50px -12px rgba(212, 175, 55, 0.25), 0 0 20px rgba(212, 175, 55, 0.1) !important;
        }
        .card-hover-effect:hover .card-image {
          transform: scale(1.12);
        }
        .btn-link {
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }
        .btn-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 1px;
          background-color: var(--primary-gold);
          transition: width 0.3s ease;
        }
        .btn-link:hover::after {
          width: 100%;
        }
        .btn-link:hover .arrow-icon {
          transform: translateX(6px);
        }
        .arrow-icon {
          transition: transform 0.3s ease;
        }
        
        .btn-whatsapp {
          background-color: #25D366;
          color: white;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }
        .btn-whatsapp:hover {
          background-color: #128C7E;
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(37, 211, 102, 0.3);
        }
        
        @media (max-width: 768px) {
          #destinations {
            padding: 60px 0 40px !important;
          }
          /* Cards: let auto-fit grid handle 1 col on small screens */
          .responsive-grid.grid-cols-3 {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          /* Fixed-height cards become auto-height on mobile */
          .card-hover-effect {
            height: auto !important;
            min-height: 380px !important;
          }
          /* Modal fills screen */
          .modal-content {
            max-height: 95dvh !important;
            padding: 0 !important;
            border-radius: 12px !important;
          }
          .modal-padding {
            padding: 16px !important;
          }
          /* Modal header image shorter on mobile */
          .modal-content > div:first-child {
            height: 200px !important;
          }
        }
      `}</style>
    </section>
  );
}
